/** @shell standard @header bar */
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  View,
  StyleSheet,
  RefreshControl,
  KeyboardAvoidingView,
  Platform,
  FlatList,
  Alert,
  ActivityIndicator,
} from 'react-native';
import Animated, { createAnimatedComponent } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useQueryClient } from '@tanstack/react-query';
import { SvgIcon } from '../components/common/SvgIcon';
import { ScreenHeader, useScreenHeaderScroll, HeaderIconButton } from '../components/common/ScreenHeader';
import { appTheme, useThemeColors, type ThemeColors } from '../shared/theme';
import {
  usePosts,
  useLikePost,
  useCreatePost,
  useAddComment,
  communityQueryKeys,
  type CommunityPost,
  type CreatePostPayload,
} from '../data/domains/community';
import { useAuthStore } from '../shared/auth/store';
import { LoadingState } from '../components/common/LoadingState';
import { ErrorState } from '../components/common/ErrorState';
import { EmptyState } from '../components/common/EmptyState';
import { PostModal } from './components/PostModal';
import { PostCard } from './components/PostCard';
import {
  StackScreenShell,
  STACK_SCROLL_GUTTER,
  STACK_SECTION_GAP,
} from '../shared/theme/studio/stackScreenShell';
import { useTranslation } from '../shared/i18n';
import { useExitOrGoBack } from '../shared/navigation/useAuthNavigation';
import { useCommunityFeatureEnabled } from '../data/domains/config';
import { androidAppHeaderContentTop } from '../shared/theme/sharedStyles';
import { SCROLL_VIEW_PROPS } from '../shared/utils/scrollListPerf';

const AnimatedFlatList = createAnimatedComponent(FlatList<CommunityPost>);
const COMMUNITY_HEADER_SIDE_SLOT = 96;

export const CommunityScreen: React.FC = () => {
  const on_back_exit = useExitOrGoBack();
  const community_enabled = useCommunityFeatureEnabled();
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const colors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createCommunityStyles(colors), [colors]);
  const { scrollY, onScroll } = useScreenHeaderScroll();
  const user = useAuthStore(s => s.user);
  const query_client = useQueryClient();
  const { data: post_list, isLoading, error, refetch, isFetching } = usePosts();
  const like_mutation = useLikePost();
  const create_mutation = useCreatePost();
  const add_comment_mutation = useAddComment();

  const [expanded_comments, set_expanded_comments] = useState<Record<number, boolean>>({});
  const [show_post_modal, set_show_post_modal] = useState(false);
  const [comment_drafts, set_comment_drafts] = useState<Record<number, string>>({});
  const [submitting_comment_post_id, set_submitting_comment_post_id] = useState<number | null>(null);

  useEffect(() => {
    if (!community_enabled) {
      on_back_exit();
    }
  }, [community_enabled, on_back_exit]);

  const handle_refresh = useCallback(() => {
    void query_client.invalidateQueries({ queryKey: communityQueryKeys.posts });
  }, [query_client]);

  const handle_like = useCallback(
    (post: CommunityPost) => {
      if (!user?.id) {
        return;
      }
      like_mutation.mutate({ postId: post.id, customerId: user.id });
    },
    [user?.id, like_mutation],
  );

  const toggle_comments = useCallback((post_id: number) => {
    set_expanded_comments(prev => ({ ...prev, [post_id]: !prev[post_id] }));
  }, []);

  const handle_create_post = useCallback(
    (data: CreatePostPayload) => {
      create_mutation.mutate(data, {
        onSuccess: () => {
          set_show_post_modal(false);
          void refetch();
        },
        onError: () => {
          Alert.alert(t('common.error'), t('community.postFailed'));
        },
      });
    },
    [create_mutation, refetch, t],
  );

  const set_comment_draft = useCallback((post_id: number, text: string) => {
    set_comment_drafts(prev => ({ ...prev, [post_id]: text }));
  }, []);

  const handle_submit_comment = useCallback(
    (post_id: number) => {
      const content = (comment_drafts[post_id] || '').trim();
      if (!content || !user?.id) {
        return;
      }
      set_submitting_comment_post_id(post_id);
      add_comment_mutation.mutate(
        { post_id, customer_id: user.id, content },
        {
          onSuccess: () => {
            set_comment_drafts(prev => ({ ...prev, [post_id]: '' }));
            void refetch();
          },
          onError: () => {
            Alert.alert(t('common.error'), t('community.commentFailed'));
          },
          onSettled: () => set_submitting_comment_post_id(null),
        },
      );
    },
    [user?.id, comment_drafts, add_comment_mutation, refetch, t],
  );

  const open_composer = useCallback(() => set_show_post_modal(true), []);

  const card_labels = useMemo(
    () => ({
      user_fallback: t('community.userFallback'),
      like: t('community.like'),
      unlike: t('community.unlike'),
      add_comment: t('community.addComment'),
      add_comment_placeholder: t('community.addCommentPlaceholder'),
      send_comment: t('community.sendComment'),
      view_comments: t('community.viewComments'),
      hide_comments: t('community.hideComments'),
      comments: t('community.comments'),
    }),
    [t],
  );

  const content_top = androidAppHeaderContentTop(insets.top, appTheme.spacing.md);
  const content_bottom = insets.bottom + 24;
  const is_refreshing = isFetching && !isLoading;

  const render_item = useCallback(
    ({ item }: { item: CommunityPost }) => (
      <PostCard
        post={item}
        colors={colors}
        expanded={expanded_comments[item.id] ?? false}
        comment_draft={comment_drafts[item.id] ?? ''}
        is_submitting_comment={submitting_comment_post_id === item.id}
        onLike={() => handle_like(item)}
        onToggleComments={() => toggle_comments(item.id)}
        onCommentDraftChange={text => set_comment_draft(item.id, text)}
        onSubmitComment={() => handle_submit_comment(item.id)}
        labels={card_labels}
      />
    ),
    [
      colors,
      expanded_comments,
      comment_drafts,
      submitting_comment_post_id,
      handle_like,
      toggle_comments,
      set_comment_draft,
      handle_submit_comment,
      card_labels,
    ],
  );

  const key_extractor = useCallback((item: CommunityPost) => String(item.id), []);

  const list_empty = useMemo(
    () => (
      <EmptyState
        icon="chat"
        title={t('community.noPostsYet')}
        message={t('community.noPostsHint')}
        actionLabel={t('community.newPost')}
        onAction={open_composer}
      />
    ),
    [t, open_composer],
  );

  const header_actions = community_enabled ? (
    <View style={styles.headerActions}>
      <HeaderIconButton
        onPress={handle_refresh}
        accessibilityLabel={t('common.refresh')}
        appearance="outline"
        disabled={is_refreshing}
        haptics>
        {is_refreshing ? (
          <ActivityIndicator size="small" color={colors.primary} />
        ) : (
          <SvgIcon name="refresh" size={20} color={colors.primary} />
        )}
      </HeaderIconButton>
      <HeaderIconButton onPress={open_composer} accessibilityLabel={t('community.newPost')} appearance="outline" haptics>
        <SvgIcon name="plus" size={22} color={colors.primary} />
      </HeaderIconButton>
    </View>
  ) : undefined;

  const header = (
    <ScreenHeader
      title={t('community.title')}
      onBack={on_back_exit}
      scrollY={scrollY}
      sideSlotWidth={community_enabled ? COMMUNITY_HEADER_SIDE_SLOT : undefined}
      rightAction={header_actions}
    />
  );

  if (!community_enabled) {
    return null;
  }

  if (isLoading) {
    return (
      <StackScreenShell colors={colors}>
        {header}
        <View style={[styles.fill, { paddingTop: content_top }]}>
          <LoadingState message={t('community.loadingPosts')} />
        </View>
      </StackScreenShell>
    );
  }

  if (error) {
    return (
      <StackScreenShell colors={colors}>
        {header}
        <View style={[styles.fill, { paddingTop: content_top }]}>
          <ErrorState
            title={t('community.failedToLoadPosts')}
            message={t('common.pleaseTryAgain')}
            onRetry={handle_refresh}
            error={error}
            issue_context={{
              page: t('community.title'),
              issue: 'Failed to load community posts',
              kind: 'api',
              api_endpoint: 'posts',
              http_method: 'GET',
            }}
          />
        </View>
      </StackScreenShell>
    );
  }

  const feed = (
    <AnimatedFlatList
      {...SCROLL_VIEW_PROPS}
      style={styles.scroll}
      data={post_list}
      keyExtractor={key_extractor}
      renderItem={render_item}
      ListEmptyComponent={list_empty}
      onScroll={onScroll}
      scrollEventThrottle={16}
      contentContainerStyle={[
        styles.scrollContent,
        { paddingTop: content_top, paddingBottom: content_bottom },
        post_list.length === 0 ? styles.scrollContentEmpty : null,
      ]}
      keyboardShouldPersistTaps="handled"
      ItemSeparatorComponent={() => <View style={styles.separator} />}
      refreshControl={
        <RefreshControl refreshing={is_refreshing} onRefresh={handle_refresh} tintColor={colors.primary} />
      }
    />
  );

  return (
    <StackScreenShell colors={colors}>
      {header}

      {Platform.OS === 'ios' ? (
        <KeyboardAvoidingView style={styles.fill} behavior="padding" keyboardVerticalOffset={0}>
          {feed}
        </KeyboardAvoidingView>
      ) : (
        <KeyboardAvoidingView style={styles.fill} behavior="height" keyboardVerticalOffset={0}>
          {feed}
        </KeyboardAvoidingView>
      )}

      <PostModal
        visible={show_post_modal}
        customerId={user?.id ?? 0}
        onClose={() => set_show_post_modal(false)}
        onSubmit={handle_create_post}
        isLoading={create_mutation.isPending}
      />
    </StackScreenShell>
  );
};

function createCommunityStyles(_c: ThemeColors) {
  return StyleSheet.create({
    fill: { flex: 1 },
    scroll: { flex: 1 },
    scrollContent: {
      paddingHorizontal: STACK_SCROLL_GUTTER,
      flexGrow: 1,
    },
    scrollContentEmpty: {
      justifyContent: 'center',
    },
    separator: {
      height: STACK_SECTION_GAP,
    },
    headerActions: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
  });
}

export default CommunityScreen;
